import React, { Fragment, useEffect, useState } from 'react';
import { Switch, Route, useLocation, useHistory } from 'react-router-dom';
import { ThemeProvider } from '@material-ui/core/styles';

import Container from 'components/atoms/Container/Container';

import TileNavigation from 'components/organisms/TileNavigation/TileNavigation';

import EatFreshSection from 'pages/EatFreshSection/EatFreshSection';
import StayActiveSection from 'pages/StayActiveSection/StayActiveSection';
import BeProductiveSection from 'pages/BeProductiveSection/BeProductiveSection';
import RestSection from 'pages/RestSection/RestSection';

import { appStructure } from 'helpers/navigationHelper';
import Box from 'components/atoms/Box/Box';
import PrivateRoute from 'authentication/PrivateRoute';
import MyProfile from 'pages/MyProfile/MyProfile';
import Onboarding from 'pages/Onboarding/Onboarding';
import { Loadable } from 'dataLayer/recoil/Loadable';
import { myProfileCompleteDataGet } from 'dataLayer/recoil/pages/myProfile/selectors';
import { traitDetailsCompleteDataGet } from 'dataLayer/recoil/pages/sections/selectors';
import TraitDetails from 'components/templates/TraitDetails/TraitDetails';
import { ROUTE_TRAIT_DETAILS } from 'routes';
import { getNextTrait, getPreviousTrait } from 'pages/helpers';
import SectionNavigation from 'components/molecules/SectionNavigation/SectionNavigation';
import Division from 'components/atoms/Division/Division';
import { apiGetUserProfile } from 'dataLayer/api/apiClient';
import { useAuth0 } from '@auth0/auth0-react';
import MyDnaProfileSection from 'pages/MyDnaProfileSection/MyDnaProfileSection';
import { getCurrentLanguageDirection } from 'helpers/rtlHelper';
import { useTranslation } from 'react-i18next';

function useQuery() {
  return new URLSearchParams(useLocation().search); //IE11 doesn't support URLSearchParams
}

export default function Home(props) {
  const [loadingProfile, setLoadingProfile] = useState(true);
  const history = useHistory();
  const { user, isAuthenticated } = useAuth0();
  const { PUBLIC_URL } = process.env;
  const { i18n } = useTranslation();

  useEffect(() => {
    if (user) {
      if (window.location.href.endsWith('onboarding') === false) {
        apiGetUserProfile()
          .then((resp) => {
            if (
              resp &&
              (resp.year_of_birth == null ||
                resp.height == null ||
                resp.weight == null ||
                resp.gender == null)
            ) {
              window.location.href = `${window.location.origin}${PUBLIC_URL}/onboarding`;
            } else if (resp && resp.extra_data && resp.extra_data.language) {
              const language = resp.extra_data.language;
              setAppLanguageFromUserProfile(language);
            }
            setLoadingProfile(false);
          })
          .catch(() => setLoadingProfile(false));
      } else {
        setLoadingProfile(false);
      }
    } else if (!isAuthenticated) {
      setLoadingProfile(false);
    }
  }, [user, isAuthenticated]);

  const setAppLanguageFromUserProfile = (language) => {
    i18n.changeLanguage(language);
    window.localStorage.setItem(
      'lan_dir_xh',
      getCurrentLanguageDirection(language)
    );
    window.localStorage.setItem('lan_xh', language);
    document.body.dir = getCurrentLanguageDirection(language);
    document.documentElement.lang = language;
  };

  const SelectedTraitDetails = (props) => {
    const { data } = props;
    const { allTraits } = data;
    let query = useQuery();
    let traitCode = query.get('code');

    const traitToDisplayIndex = allTraits.findIndex((item) => {
      return item.trait.code === traitCode;
    });

    const traitToDisplay = allTraits[traitToDisplayIndex];
    const previousTraitCode = getPreviousTrait(allTraits, traitToDisplayIndex);
    const nextTraitCode = getNextTrait(allTraits, traitToDisplayIndex);

    const {
      trait,
      level_label,
      num_genetic_variations,
      genetic_variations_selection,
      references,
      percentile,
    } = traitToDisplay;
    const { name, description } = trait;

    const traitDetailsData = {
      title: name,
      text: description,
      variations: genetic_variations_selection,
      references: references,
      item: {
        firstCircle: {
          label: level_label,
        },
        secondCircle: {
          label: 'Genetic variations',
          value: num_genetic_variations,
        },
        thirdCircle: {
          label: 'Population percentile',
          value: percentile,
        },
      },
    };

    return (
      <Fragment>
        <Division
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <SectionNavigation
            secondary
            title={name}
            onClickPrevious={() => {
              history.push(`${ROUTE_TRAIT_DETAILS}?code=${previousTraitCode}`);
            }}
            onClickNext={() => {
              history.push(`${ROUTE_TRAIT_DETAILS}?code=${nextTraitCode}`);
            }}
          />
        </Division>
        <TraitDetails {...traitDetailsData} />
      </Fragment>
    );
  };

  return (
    <Fragment>
      <Container>
        {!loadingProfile && (
          <Box mt={3}>
            <Switch>
              <PrivateRoute path={appStructure.sections[0].route}>
                <ThemeProvider theme={appStructure.sections[0].theme}>
                  <MyDnaProfileSection />
                </ThemeProvider>
              </PrivateRoute>

              <PrivateRoute path={appStructure.sections[1].route}>
                <ThemeProvider theme={appStructure.sections[1].theme}>
                  <EatFreshSection />
                </ThemeProvider>
              </PrivateRoute>

              {/*Start of temporary routes used to show new api endpoint*/}
              <PrivateRoute path={appStructure.demoMyBreakfast.route}>
                <ThemeProvider theme={appStructure.sections[1].theme}>
                  <EatFreshSection />
                </ThemeProvider>
              </PrivateRoute>

              <PrivateRoute path={appStructure.demoMyLunch.route}>
                <ThemeProvider theme={appStructure.sections[1].theme}>
                  <EatFreshSection />
                </ThemeProvider>
              </PrivateRoute>

              <PrivateRoute path={appStructure.demoMyDinner.route}>
                <ThemeProvider theme={appStructure.sections[1].theme}>
                  <EatFreshSection />
                </ThemeProvider>
              </PrivateRoute>

              {/*End of temporary routes used to show new api endpoint*/}

              <PrivateRoute path={appStructure.sections[2].route}>
                <ThemeProvider theme={appStructure.sections[2].theme}>
                  <StayActiveSection />
                </ThemeProvider>
              </PrivateRoute>

              <PrivateRoute path={appStructure.sections[3].route}>
                <ThemeProvider theme={appStructure.sections[3].theme}>
                  <BeProductiveSection />
                </ThemeProvider>
              </PrivateRoute>

              <PrivateRoute path={appStructure.sections[4].route}>
                <ThemeProvider theme={appStructure.sections[4].theme}>
                  <RestSection />
                </ThemeProvider>
              </PrivateRoute>

              <Route path={appStructure.home.route} exact>
                <TileNavigation data={appStructure && appStructure.sections} />
              </Route>

              <PrivateRoute
                path={appStructure.traitDetails.route}
                render={() => (
                  <Loadable
                    value={traitDetailsCompleteDataGet}
                    component={SelectedTraitDetails}
                  />
                )}
              />

              <PrivateRoute
                exact
                path={appStructure.myProfile.route}
                render={() => (
                  <Loadable
                    value={myProfileCompleteDataGet}
                    component={MyProfile}
                  />
                )}
              />
              <PrivateRoute
                exact
                path={appStructure.onboarding.route}
                render={() => (
                  <Loadable
                    value={myProfileCompleteDataGet}
                    component={Onboarding}
                  />
                )}
              />
            </Switch>
          </Box>
        )}
      </Container>
    </Fragment>
  );
}
